import React from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, GraduationCap, Lock } from 'lucide-react';

// Shared grading student selector: group filter chips + current-student card with prev/next
// + a horizontal strip of student-name chips. The grading form itself is rendered by the parent
// below this selector. Navigation/selection is delegated to onSelectStudent so the parent can
// auto-save / acquire locks before switching.
export const GradingStudentSelector = ({
    students,             // enrolled students (sorted, full list)
    groups = [],
    groupFilter = 'all',
    onGroupFilter,
    currentStudentId,
    onSelectStudent,      // (sid) => void
    gradedStudentIds = [],
    lockedStudentIds = [],
    rightSlot = null,     // e.g. total score
    statusSlot = null,    // e.g. dirty indicator + save button
}) => {
    const inGroup = (s) => groupFilter === 'all' || (groups.find(g => g.id === groupFilter)?.student_ids || []).includes(s.id);
    const filtered = students.filter(inGroup);
    const curIdx = filtered.findIndex(s => s.id === currentStudentId);
    const current = filtered[curIdx];
    const gradedSet = new Set(gradedStudentIds);
    const lockedSet = new Set(lockedStudentIds);

    const go = (delta) => {
        const next = filtered[curIdx + delta];
        if (next) onSelectStudent(next.id);
    };

    return (
        <div className="space-y-4">
            {/* Group filter */}
            {groups.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => onGroupFilter('all')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${groupFilter === 'all' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        data-testid="grade-group-filter-all">
                        All ({students.length})
                    </button>
                    {groups.map(g => (
                        <button key={g.id} onClick={() => onGroupFilter(g.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${groupFilter === g.id ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            data-testid={`grade-group-filter-${g.id}`}>
                            {g.name} ({(g.student_ids || []).filter(sid => students.some(s => s.id === sid)).length})
                        </button>
                    ))}
                </div>
            )}

            {/* Current student card + prev/next */}
            <div className="flex items-center gap-4">
                <div className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 px-4 py-3 flex-1">
                    <div className="w-10 h-10 bg-ica-navy/10 rounded-full flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-ica-navy dark:text-ica-light-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-slate-100 text-sm truncate">{current?.name || 'Select a student'}</p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">{current?.student_id || ''}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => go(-1)} disabled={curIdx <= 0} data-testid="grade-prev" title="Previous Student">
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-xs text-gray-500 min-w-[50px] text-center">{curIdx >= 0 ? curIdx + 1 : 0} / {filtered.length}</span>
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => go(1)} disabled={curIdx >= filtered.length - 1} data-testid="grade-next" title="Next Student">
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
                {rightSlot}
            </div>

            {/* Student chips */}
            <div className="flex gap-1.5 flex-wrap">
                {filtered.map((s) => {
                    const isCurrent = s.id === currentStudentId;
                    const hasGrade = gradedSet.has(s.id);
                    const isLocked = lockedSet.has(s.id);
                    return (
                        <button key={s.id} onClick={() => onSelectStudent(s.id)}
                            data-testid={`grade-student-chip-${s.id}`}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                                isCurrent ? 'bg-ica-gold text-white'
                                : hasGrade ? 'bg-green-50 text-green-700 border border-green-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}>
                            {isLocked && <Lock className="w-3 h-3" />}{s.name.split(' ')[0]}
                        </button>
                    );
                })}
            </div>

            {statusSlot}
        </div>
    );
};

export default GradingStudentSelector;
